/**
 * 捐赠记录 — legacy AP兑捐UO donation history (image 3).
 * URL: /donations/records
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apAPI } from '../api/ap';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedPanel } from '../components/ui/ThemedSurface';
import { DonationSummaryCard } from '../components/donations/DonationSummaryCard';

function fmtTime(value: string): string {
  const trimmed = (value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  const date = new Date(trimmed.endsWith('Z') ? trimmed : `${trimmed.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return trimmed;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function fmtAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function DonationRecordsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: preview } = useQuery({
    queryKey: ['ap', 'donate-uo', 'preview'],
    queryFn: () => apAPI.donatePreview(),
    staleTime: 30_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ap', 'donate-uo', 'mine'],
    queryFn: () => apAPI.donateMine({ page: 1, limit: 50 }),
    staleTime: 30_000,
  });

  const rows = data?.rows ?? [];

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('donate.records', '捐赠记录')}
          onBack={() => navigate(-1)}
          theme={C}
        />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '24px' }}
        >
          <DonationSummaryCard
            summary={preview}
            labels={{
              level: t('donate.donorLevel', '捐赠者级别'),
              amount: t('donate.donatedAmount', '捐赠数量'),
              value: t('donate.donatedValue', '捐赠价值'),
            }}
          />

          <div className="text-sm font-semibold" style={{ color: C.text }}>
            {t('donate.records', '捐赠记录')}
          </div>

          {isLoading && !data ? (
            <div className="flex flex-col gap-3">
              <LoadingSkeleton className="h-20" />
              <LoadingSkeleton className="h-20" />
            </div>
          ) : rows.length === 0 ? (
            <ThemedPanel className="p-4">
              <EmptyState title={t('donate.noRecords', '还没有捐赠记录')} className="py-8" />
            </ThemedPanel>
          ) : (
            <div className="flex flex-col gap-3">
              {rows.map((row) => (
                <ThemedPanel key={row.id} className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold" style={{ color: C.gold }}>
                        {t('donate.recordId', 'ID')}：{row.account_number || row.id}
                      </div>
                      <div className="mt-1 text-xs" style={{ color: C.muted }}>
                        {fmtTime(row.create_time)}
                      </div>
                    </div>
                    <div
                      className="shrink-0 font-mono text-lg font-semibold tabular-nums"
                      style={{ color: C.text }}
                    >
                      {fmtAmount(row.uo_credited)} ORB
                    </div>
                  </div>
                </ThemedPanel>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DonationRecordsPage;
